import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash, CaretDown } from "@phosphor-icons/react";

const BLANK_ROW = { name: "", category: "", schedule: "", dose: "", duration: "", instructions: "" };

/**
 * MedicineRxList — multi-row medicine prescription editor used both during
 * hospitalization (progress notes) and on the discharge summary.
 *
 * Each row supports:
 *  - Name (autocomplete against /medicines master)
 *  - Category (auto-filled when picking from suggestions; otherwise free)
 *  - When to take / Schedule (autocomplete + free text)
 *  - Dose, Duration, Instructions (free text)
 *
 * value: list of rows (PrescribedMedicine[])
 * onChange: replaces the whole list
 */
export default function MedicineRxList({ value, onChange, title = "Medicines", testidPrefix = "rx" }) {
  const rows = Array.isArray(value) ? value : [];
  const [medicines, setMedicines] = useState([]);
  const [schedules, setSchedules] = useState([]);

  useEffect(() => {
    api.get("/medicines").then((r) => setMedicines(r.data || [])).catch(() => {});
    api.get("/medicine-schedules").then((r) => setSchedules(r.data || [])).catch(() => {});
  }, []);

  // Seed a single blank row by default so the user can start typing immediately.
  useEffect(() => {
    if (!Array.isArray(value) || value.length === 0) {
      onChange([{ ...BLANK_ROW }]);
    }
  }, []);

  const update = (idx, patch) => {
    const next = rows.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const addRow = () => onChange([...rows, { ...BLANK_ROW }]);
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));

  return (
    <div data-testid={`${testidPrefix}-list`}>
      <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)] block mb-2">{title}</Label>
      {rows.length > 0 && (
        <div className="space-y-2">
          {rows.map((row, idx) => (
            <Row
              key={idx} idx={idx} row={row} testidPrefix={testidPrefix}
              medicines={medicines} schedules={schedules}
              onChange={(patch) => update(idx, patch)}
              onRemove={() => removeRow(idx)}
            />
          ))}
        </div>
      )}
      <div className="mt-2">
        <Button type="button" size="sm" variant="outline" onClick={addRow} data-testid={`${testidPrefix}-add-row`} className="rounded-sm h-8 px-3 text-xs border-[var(--hms-border-2)] bg-[var(--hms-paper)]">
          <Plus size={12} weight="bold" className="mr-1" /> Add row
        </Button>
      </div>
    </div>
  );
}

function Row({ idx, row, testidPrefix, medicines, schedules, onChange, onRemove }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-end bg-[var(--hms-paper)]/40 border border-[var(--hms-border)] rounded-sm p-2" data-testid={`${testidPrefix}-row-${idx}`}>
      <NameField row={row} idx={idx} testidPrefix={testidPrefix} medicines={medicines} onPick={onChange} onChange={(v) => onChange({ name: v })} />
      <Cell label="Category" col={2}>
        <Input value={row.category || ""} onChange={(e) => onChange({ category: e.target.value })} placeholder="Tablet…" data-testid={`${testidPrefix}-${idx}-category`} className="h-8 rounded-sm border-[var(--hms-border-2)] bg-white text-xs" />
      </Cell>
      <ScheduleField row={row} idx={idx} testidPrefix={testidPrefix} schedules={schedules} onChange={(v) => onChange({ schedule: v })} />
      <Cell label="Dose" col={1}>
        <Input value={row.dose || ""} onChange={(e) => onChange({ dose: e.target.value })} placeholder="1 tab" data-testid={`${testidPrefix}-${idx}-dose`} className="h-8 rounded-sm border-[var(--hms-border-2)] bg-white text-xs" />
      </Cell>
      <Cell label="Duration" col={1}>
        <Input value={row.duration || ""} onChange={(e) => onChange({ duration: e.target.value })} placeholder="5 days" data-testid={`${testidPrefix}-${idx}-duration`} className="h-8 rounded-sm border-[var(--hms-border-2)] bg-white text-xs" />
      </Cell>
      <Cell label="Instructions" col={2}>
        <Input value={row.instructions || ""} onChange={(e) => onChange({ instructions: e.target.value })} placeholder="With water" data-testid={`${testidPrefix}-${idx}-instructions`} className="h-8 rounded-sm border-[var(--hms-border-2)] bg-white text-xs" />
      </Cell>
      <div className="col-span-1 flex justify-end">
        <Button type="button" size="sm" variant="ghost" onClick={onRemove} data-testid={`${testidPrefix}-${idx}-remove`} className="rounded-sm h-8 px-2 text-[var(--hms-accent)]" title="Remove row">
          <Trash size={14} weight="bold" />
        </Button>
      </div>
    </div>
  );
}

function Cell({ label, col, children }) {
  return (
    <div className={`col-span-${col}`}>
      <div className="text-[9px] uppercase tracking-wider font-bold text-[var(--hms-muted)] mb-0.5">{label}</div>
      {children}
    </div>
  );
}

/* ---------- Autocomplete: Medicine Name ---------- */
function NameField({ row, idx, testidPrefix, medicines, onPick, onChange }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const q = (row.name || "").trim().toLowerCase();

  const matches = useMemo(() => {
    if (!q) return medicines.slice(0, 8);
    return medicines
      .filter((m) => (m.name || "").toLowerCase().includes(q) || (m.generic_name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [q, medicines]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (m) => {
    onPick({ name: m.name, category: m.category || row.category || "" });
    setOpen(false);
  };

  return (
    <div className="col-span-3 relative" ref={boxRef}>
      <div className="text-[9px] uppercase tracking-wider font-bold text-[var(--hms-muted)] mb-0.5">Medicine *</div>
      <div className="relative">
        <Input
          value={row.name || ""}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Start typing…"
          required
          data-testid={`${testidPrefix}-${idx}-name`}
          className="h-8 rounded-sm border-[var(--hms-border-2)] bg-white text-xs pr-6"
          autoComplete="off"
        />
        <CaretDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--hms-muted)] pointer-events-none" />
      </div>
      {open && matches.length > 0 && (
        <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-[var(--hms-border-2)] rounded-sm shadow-lg max-h-60 overflow-auto" data-testid={`${testidPrefix}-${idx}-name-list`}>
          {matches.map((m) => (
            <button
              key={m.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(m)}
              data-testid={`${testidPrefix}-${idx}-name-opt-${m.id}`}
              className="w-full text-left px-2 py-1.5 hover:bg-[var(--hms-paper)] border-b border-[var(--hms-border)] last:border-0 text-xs"
            >
              <div className="font-medium">{m.name}</div>
              <div className="text-[10px] text-[var(--hms-muted)]">
                {m.category || "—"}{m.generic_name ? ` · ${m.generic_name}` : ""}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Autocomplete: Schedule ---------- */
function ScheduleField({ row, idx, testidPrefix, schedules, onChange }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const q = (row.schedule || "").trim().toLowerCase();

  const matches = useMemo(() => {
    if (!q) return schedules.slice(0, 8);
    return schedules.filter((s) => (s.label || "").toLowerCase().includes(q)).slice(0, 8);
  }, [q, schedules]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div className="col-span-2 relative" ref={boxRef}>
      <div className="text-[9px] uppercase tracking-wider font-bold text-[var(--hms-muted)] mb-0.5">When to take</div>
      <div className="relative">
        <Input
          value={row.schedule || ""}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="1-0-1"
          data-testid={`${testidPrefix}-${idx}-schedule`}
          className="h-8 rounded-sm border-[var(--hms-border-2)] bg-white text-xs font-mono pr-6"
          autoComplete="off"
        />
        <CaretDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--hms-muted)] pointer-events-none" />
      </div>
      {open && matches.length > 0 && (
        <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-[var(--hms-border-2)] rounded-sm shadow-lg max-h-60 overflow-auto" data-testid={`${testidPrefix}-${idx}-schedule-list`}>
          {matches.map((s) => (
            <button
              key={s.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(s.label); setOpen(false); }}
              data-testid={`${testidPrefix}-${idx}-schedule-opt-${s.id}`}
              className="w-full text-left px-2 py-1.5 hover:bg-[var(--hms-paper)] border-b border-[var(--hms-border)] last:border-0 text-xs"
            >
              <div className="font-mono font-medium">{s.label}</div>
              {s.notes && <div className="text-[10px] text-[var(--hms-muted)]">{s.notes}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
